import SEO from "@/components/SEO";
import { PageHero, Reveal, Section, Rich, UL, Callout } from "@/components/Bits";
import { ShieldCheck, KeyRound, Ban, Siren } from "lucide-react";

const PILLARS = [
  { Icon: ShieldCheck, title: "Verified helpers", body: "Aadhaar / PAN checks plus address validation before activation. Verification may be repeated periodically; forged documents mean a permanent ban." },
  { Icon: KeyRound, title: "OTP-secured jobs", body: "Work starts and finishes only after the Customer shares an OTP — a permanent record of every job's beginning and end." },
  { Icon: Ban, title: "High-risk work excluded", body: "Categories that create fraud risk (e.g. bank cash-handling) have been permanently excluded from the Platform for safety." },
  { Icon: Siren, title: "Emergency flow", body: "Urgent safety issues are triaged 24×7 through the in-app “Emergency Assistance” flow." },
];

const PROHIBITED = [
  "Handling, carrying, depositing, withdrawing or transferring CASH on someone else’s behalf.",
  "Handling, carrying or storing GOLD, silver, jewellery or other precious items.",
  "Sharing or entering another person’s PASSWORD, PIN, ATM/DEBIT/CREDIT card details, UPI PIN, netbanking credentials or OTP.",
  "Physical possession or use of another person’s ATM/debit/credit card, cheque book, passbook or signed blank cheques.",
  "Carrying highly sensitive originals (original property papers, sole-copy government IDs, legal case files) unless the Customer accompanies the Middle Man or authorised courier processes are used.",
  "Any transaction that a reasonable person would consider financially risky or that the platform has flagged as prohibited.",
];

export default function Safety() {
  return (
    <div data-testid="safety-page">
      <SEO
        title="Safety"
        description="Middle Man safety — Aadhaar/PAN verified helpers, OTP-secured jobs, live tracking, and the prohibited high-risk work list (cash, gold, OTPs, ATM cards, cheque books)."
        path="/safety"
      />
      <PageHero
        kicker="Safety"
        title="Safety is the system, not a feature."
        subtitle="Middle Man is a coordinator, not a bank, courier, or valuables agent. Here's exactly how we keep customers and Middle Men protected."
      />

      <div className="mx-auto max-w-7xl px-6 py-16 md:py-24">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="safety-pillars">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <div className="h-full border border-line bg-white p-7">
                <p.Icon className="h-6 w-6 text-brand" strokeWidth={1.7} />
                <h2 className="mt-5 font-serif text-lg font-semibold">{p.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-mute">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mx-auto mt-20 max-w-3xl">
          <div className="space-y-14">
            <Reveal>
              <Section title="What NOT to ask a Middle Man to do">
                <Rich text="For your own protection, please do NOT assign — and Middle Men will not accept — any work involving:" />
                <UL items={[
                  "Cash: handling, carrying, depositing, withdrawing or transferring cash on your behalf.",
                  "Gold, jewellery or other precious items.",
                  "ATM/debit/credit cards, cheque books, UPI PINs, netbanking passwords or OTPs.",
                  "Original property papers or sole-copy government IDs (unless the customer accompanies the Middle Man).",
                ]} />
              </Section>
            </Reveal>

            <Reveal>
              <Section title="High-risk work — strictly discouraged">
                <Rich text="To protect both Customers and Middle Men, you MUST NOT assign or accept any work that involves the following high-risk items or actions:" />
                <UL items={PROHIBITED} />
              </Section>
            </Reveal>

            <Reveal>
              <Section title="Prohibited goods and activities">
                <Rich text="The following are strictly prohibited on the platform regardless of how a Work Request is described (including if described as an “errand”):" />
                <UL items={[
                  "Weapons, ammunition, explosives, fireworks",
                  "Narcotics, psychotropic substances and drugs prohibited under law",
                  "Live animals, plants or biological material where transport requires a licence",
                  "Hazardous chemicals, flammables, corrosives and radioactive material",
                  "Cash in transit above the limit permitted by law",
                  "Stolen goods, counterfeit currency or documents",
                  "Human remains, body parts or medical samples requiring a licensed courier",
                  "Items whose sale, possession or transport is restricted by Indian law",
                  "Prescription medicines, tobacco/nicotine and alcohol in the product marketplace",
                ]} />
                <Rich text="The platform reserves the right to refuse, cancel or report any Work Request or listing that reasonably appears to involve prohibited goods or activities." />
              </Section>
            </Reveal>

            <Reveal>
              <div className="border-2 border-brand bg-white p-8 md:p-10" data-testid="safety-golden-rule">
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand">The golden rule</p>
                <p className="mt-4 font-serif text-2xl font-semibold leading-snug tracking-tight md:text-3xl">
                  If a task involves cash, gold, passwords, OTPs or ATM cards — don't assign it, don't accept it.
                </p>
                <p className="mt-4 text-sm leading-relaxed text-mute">
                  Report suspicious requests to{" "}
                  <a href="mailto:info.touristjunction@gmail.com" className="font-semibold text-brand">info.touristjunction@gmail.com</a>.
                  {" "}Middle Man Platform is a coordinator; it will not be liable for losses arising from prohibited
                  high-risk work performed off-platform or in breach of the Terms.
                </p>
              </div>
            </Reveal>

            <Reveal>
              <Callout>
                <Rich text="Keep your password / OTP confidential; never share them with a Middle Man or anyone else. Always create your job via the in-app **Send Work** flow so OTP-secured start/finish, live tracking and one-tap dispute resolution stay active." />
              </Callout>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
